/**
 * "Add to Home Screen" guide, tailored to the device (iPadOS/iOS 26 Safari, older iOS, Android
 * Chrome, desktop). Used full-screen right after an invite link is accepted, on /install and in
 * a sheet on the sign-in screen. Renders no page heading — the host provides one.
 */
import {
  Check,
  CircleCheck,
  Download,
  EllipsisVertical,
  Info,
  Laptop,
  MonitorDown,
  Share,
  Smartphone,
  SquarePlus,
  ToggleRight,
  type LucideIcon,
} from 'lucide-react';
import { useState, useSyncExternalStore } from 'react';
import { useT, type MessageKey } from '../i18n';
import { isIOS, isStandalone } from '../lib/standalone';
import { Button, Card, ChipGroup } from './ui';

/* ─── Android/desktop Chromium install prompt (captured as early as possible) ─────────── */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<() => void>();
function notifyPrompt() {
  for (const fn of promptListeners) fn();
}
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    deferredPrompt = e as BeforeInstallPromptEvent;
    notifyPrompt();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notifyPrompt();
  });
}
function subscribePrompt(fn: () => void) {
  promptListeners.add(fn);
  return () => {
    promptListeners.delete(fn);
  };
}
function useInstallPrompt(): BeforeInstallPromptEvent | null {
  return useSyncExternalStore(
    subscribePrompt,
    () => deferredPrompt,
    () => null,
  );
}

/* ─── Platform detection ────────────────────────────────────────────────── */

export type InstallPlatform = 'ios26' | 'ios' | 'android' | 'desktop';

/** Safari 26 reports "Version/26"; the OS version in the UA is frozen, so it is only a fallback. */
export function detectInstallPlatform(ua: string = navigator.userAgent): InstallPlatform {
  const ios = isIOS();
  if (ios) {
    const safari = /Version\/(\d+)/.exec(ua);
    const os = /OS (\d+)_\d/.exec(ua);
    const v = safari ? Number(safari[1]) : os ? Number(os[1]) : 0;
    return v >= 26 ? 'ios26' : 'ios';
  }
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

function isIOSNonSafari(ua: string = navigator.userAgent): boolean {
  return isIOS() && /CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|DuckDuckGo/.test(ua);
}

interface Step {
  icon: LucideIcon;
  text: MessageKey;
}

const STEPS: Record<InstallPlatform, Step[]> = {
  ios26: [
    { icon: Share, text: 'auth.install.ios26.1' },
    { icon: SquarePlus, text: 'auth.install.ios26.2' },
    { icon: ToggleRight, text: 'auth.install.ios26.3' },
    { icon: Smartphone, text: 'auth.install.open' },
  ],
  ios: [
    { icon: Share, text: 'auth.install.ios.1' },
    { icon: SquarePlus, text: 'auth.install.ios.2' },
    { icon: Check, text: 'auth.install.ios.3' },
    { icon: Smartphone, text: 'auth.install.open' },
  ],
  android: [
    { icon: EllipsisVertical, text: 'auth.install.android.1' },
    { icon: Download, text: 'auth.install.android.2' },
    { icon: Check, text: 'auth.install.android.3' },
    { icon: Smartphone, text: 'auth.install.open' },
  ],
  desktop: [
    { icon: MonitorDown, text: 'auth.install.desktop.1' },
    { icon: Laptop, text: 'auth.install.desktop.2' },
    { icon: Check, text: 'auth.install.desktop.3' },
  ],
};

const PLATFORM_LABEL: Record<InstallPlatform, MessageKey> = {
  ios26: 'auth.install.p.ios26',
  ios: 'auth.install.p.ios',
  android: 'auth.install.p.android',
  desktop: 'auth.install.p.desktop',
};

function StepItem({ n, step }: { n: number; step: Step }) {
  const t = useT();
  return (
    <li>
      <Card className="flex items-start gap-4 p-4 md:p-5">
        <span
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-[20px] font-bold text-[var(--accent-ink)]"
          aria-hidden
        >
          {n}
        </span>
        <span className="sr-only">{t('auth.install.step', { n })}</span>
        <span
          className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-[var(--line)] bg-[var(--surface-2)] text-[var(--accent)]"
          aria-hidden
        >
          <step.icon className="size-6" />
        </span>
        <p className="min-w-0 flex-1 self-center text-[18px] leading-snug">{t(step.text)}</p>
      </Card>
    </li>
  );
}

export function InstallGuide({ onContinue }: { onContinue?: () => void }) {
  const t = useT();
  const [platform, setPlatform] = useState<InstallPlatform>(() => detectInstallPlatform());
  const prompt = useInstallPrompt();
  const [prompting, setPrompting] = useState(false);
  const standalone = isStandalone();

  const runPrompt = async () => {
    if (!prompt) return;
    setPrompting(true);
    try {
      await prompt.prompt();
      await prompt.userChoice;
    } catch {
      /* prompt can only be used once; the steps below still apply */
    } finally {
      deferredPrompt = null;
      notifyPrompt();
      setPrompting(false);
    }
  };

  if (standalone) {
    return (
      <div className="flex flex-col gap-5">
        <Card className="flex items-start gap-4 p-5">
          <CircleCheck className="size-8 shrink-0 text-[var(--ok-text)]" aria-hidden />
          <div>
            <h2 className="text-xl font-semibold">{t('auth.install.installed_title')}</h2>
            <p className="mt-1 text-[var(--ink-2)]">{t('auth.install.installed_body')}</p>
          </div>
        </Card>
        {onContinue ? (
          <Button size="lg" block onClick={onContinue}>
            {t('auth.onb.next')}
          </Button>
        ) : null}
      </div>
    );
  }

  const steps = STEPS[platform];
  const options = (Object.keys(PLATFORM_LABEL) as InstallPlatform[]).map((p) => ({ value: p, label: t(PLATFORM_LABEL[p]) }));

  return (
    <div className="flex flex-col gap-5">
      <p className="text-[18px] leading-relaxed text-[var(--ink-2)]">{t('auth.install.why')}</p>

      <ChipGroup single label={t('auth.install.platform')} options={options} value={[platform]} onChange={(v) => v[0] && setPlatform(v[0])} />

      {(platform === 'android' || platform === 'desktop') && prompt ? (
        <Button size="lg" block icon={Download} busy={prompting} onClick={() => void runPrompt()}>
          {t('auth.install.android_button')}
        </Button>
      ) : null}

      <ol className="flex flex-col gap-3" aria-label={t('auth.install.title')}>
        {steps.map((s, i) => (
          <StepItem key={s.text} n={i + 1} step={s} />
        ))}
      </ol>

      {(platform === 'ios' || platform === 'ios26') && isIOSNonSafari() ? (
        <p className="flex items-start gap-3 rounded-xl bg-amber-100 px-4 py-3 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100">
          <Info className="mt-0.5 size-5 shrink-0" aria-hidden />
          <span>{t('auth.install.ios_other_browser')}</span>
        </p>
      ) : null}

      <p className="flex items-start gap-3 rounded-xl bg-[var(--accent-soft)] px-4 py-3 text-[var(--ink)]">
        <Info className="mt-0.5 size-5 shrink-0 text-[var(--accent)]" aria-hidden />
        <span>{t('auth.install.signin_note')}</span>
      </p>

      {onContinue ? (
        <Button variant="secondary" size="lg" block onClick={onContinue}>
          {t('auth.accept.continue_browser')}
        </Button>
      ) : null}
    </div>
  );
}
